import { Trans, useTranslation } from "react-i18next";
import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Box, ChevronRight, Eye, FileText, FolderPlus, Folder as FolderIcon, ImageIcon, Loader2, Music, Upload, Video, X } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { DeferredActionButton } from "@/components/ui/deferred-action-button";
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { sideDragProps } from "../manual-editor/side-drag.js";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { AssetFile, AssetFolderNode, AssetKind } from "@arc/contracts";
import { ASSET_UPLOAD_ACCEPT_EXTENSIONS } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useAssetFolder, type AssetScope } from "./useAssetLibrary.js";
import { useUploadAsset } from "./useUploadAsset.js";

const KIND_ICON: Record<AssetKind, typeof FileText> = { image: ImageIcon, video: Video, model3d: Box, audio: Music, other: FileText };

function fmtSize(b: number | null): string {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1_048_576) return `${Math.round(b / 1024)} kb`;
  return `${Math.round(b / 1_048_576)} mb`;
}

/**
 * The **asset library** (ADR-024 Phase 11a) as a **right-side overlay drawer** (owner decision #6) — it slides OVER the manual
 * editor without pushing it (the shared {@link Drawer} is a fixed `ModalOverlay`). Mirrors the legacy `DisplayFileSelection`
 * **row list** (folders then files: icon · name · size · 👁 preview), the **type filter** + "only selectable" toggle, and the
 * Master ("All manuals") scope. Two modes: **manage** (browse/preview, from the editor rail) and **pick** (`onSelect`, from a
 * step's Add/Replace media). Reads the real `GET /assets/folders` (Master scope) via {@link useAssetFolder}.
 */
export function AssetLibraryDrawer({
  session,
  isOpen,
  onClose,
  mode,
  types = null,
  onSelect,
  overlayClassName,
  manualId = null,
  docked = false,
  title,
  hint,
}: {
  session: AuthSession | null;
  isOpen: boolean;
  onClose: () => void;
  mode: "manage" | "pick";
  /** Allowed extensions for pick mode (e.g. `[".jpg",".png",".mp4"]`); null ⇒ everything selectable. */
  types?: string[] | null;
  onSelect?: (file: AssetFile) => void;
  /** Backdrop z-index override — a `z-[80]` bump when this picker opens ABOVE a full-screen editor (the collage, z-[70]). */
  overlayClassName?: string;
  /** The manual in context (#217 P2) — enables the "This manual" (project files) scope tab. null ⇒ Master-only. */
  manualId?: number | null;
  /** A column beside the editor instead of a modal over it. */
  docked?: boolean;
  /** Overrides the default title. */
  title?: string;
  /** A line under the title. */
  hint?: string;
}) {
  const { t } = useTranslation();
  // Scope (#217 P2): the realm-wide Master pool ("All manuals") or THIS manual's own project folder. `folderId` resets to the
  // scope root (null) whenever the scope changes — the two subtrees have disjoint folder ids.
  // From a manual (the editor) its own files come first (owner 2026-11-04); the general library is one tab away.
  const [scopeKind, setScopeKind] = useState<"master" | "manual">(manualId != null ? "manual" : "master");
  const [folderId, setFolderId] = useState<number | null>(null); // null ⇒ the current scope's root
  const [onlySelectable, setOnlySelectable] = useState(false);
  const [preview, setPreview] = useState<AssetFile | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);

  const scope: AssetScope = scopeKind === "manual" && manualId != null ? { kind: "manual", manualId } : { kind: "master" };
  // [ADR-043 D5] In a filtered PICK the upload must offer only what can then be picked. The input used to accept the
  // WHOLE asset list regardless of `types`, so an author in an images-only pick could upload a `.mp4` and discover
  // only afterwards that it is unselectable — the dead end the accept-list contract comment exists to prevent.
  //
  // It must be the INTERSECTION with what the server will actually store, not `types` alone (diff panel). A 3D-model
  // pick asks for `.glb/.gltf/.obj/.fbx`, and NONE of those are in the upload allow-list — narrowing to `types` there
  // would offer an OS file dialog in which every choice 415s, which is worse than the old behaviour. When the
  // intersection is empty, nothing here is uploadable at all and the control is hidden rather than left to fail.
  const uploadAccept = (types && types.length > 0 ? types.filter((t) => ASSET_UPLOAD_ACCEPT_EXTENSIONS.includes(t.toLowerCase())) : [...ASSET_UPLOAD_ACCEPT_EXTENSIONS]).join(",");
  const canUpload = uploadAccept !== "";
  const switchScope = (kind: "master" | "manual"): void => {
    if (kind === scopeKind) return; // diff-panel P2w-F2: re-clicking the ACTIVE tab is a no-op (don't reset folder navigation)
    setScopeKind(kind);
    setFolderId(null);
    setUploadedName(null);
  };

  const { data } = useAssetFolder(session, scope, folderId, types);
  const crumbs = data?.breadcrumb ?? [];
  const noProjectFolder = scopeKind === "manual" && data != null && data.folder === null; // a manual with no folder yet

  // Upload (ADR-025 A2 + #217 P2) — a hidden file input driven by the footer button; the picked image streams to the CURRENT
  // folder in the CURRENT scope (Master, or the manual's own folder — lazy-created server-side on the first project upload).
  const upload = useUploadAsset(session, scope, folderId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onPickFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file fires onChange again
    if (!file) return;
    setUploadedName(null);
    upload.mutate(file, { onSuccess: (res) => setUploadedName(res.name) });
  };

  const files = (data?.files ?? []).filter((f) => (onlySelectable ? f.selectable : true));
  const folders = data?.folders ?? [];

  return (
    <Drawer isOpen={isOpen} onOpenChange={(o) => !o && onClose()} size="xl" docked={docked} className="gap-0 p-0" overlayClassName={overlayClassName}>
      <DrawerHeader className="flex-col gap-3 px-5 pt-5 pb-3">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="min-w-0">
            <DrawerTitle>{title ?? t("assetLibrary.assetLibrary")}</DrawerTitle>
            {hint ? <p className="text-muted-foreground m-0 text-xs">{hint}</p> : null}
          </div>
          <Button variant="ghost" size="icon" aria-label={t("assetLibrary.closeAssetLibrary")} onPress={onClose}>
            <X aria-hidden="true" />
          </Button>
        </div>
        {/* Scope tabs — "This manual" (the manual's own project folder, #217 P2) + Master ("All manuals"). The project tab is
            enabled only in a manual context (`manualId`); otherwise it's a disabled hint. */}
        <div role="tablist" aria-label={t("assetLibrary.assetScope")} className="bg-muted inline-flex rounded-lg p-0.5 text-sm">
          {manualId != null ? (
            // Disabled while an upload is in flight (diff-panel P2w-F3): switching scope mid-upload would attribute the
            // in-flight upload's success line to the wrong tab.
            <ScopeTab selected={scopeKind === "manual"} disabled={upload.isPending} onSelect={() => switchScope("manual")}>
              {t("assetLibrary.thisManual")}
            </ScopeTab>
          ) : (
            <TooltipTrigger delay={0}>
              <span role="tab" aria-selected="false" aria-disabled="true" tabIndex={0} className="text-muted-foreground cursor-not-allowed rounded-md px-3 py-1 opacity-60">
                {t("assetLibrary.thisManual")}
              </span>
              <Tooltip>{t("assetLibrary.openManualForFiles")}</Tooltip>
            </TooltipTrigger>
          )}
          <ScopeTab selected={scopeKind === "master"} disabled={upload.isPending} onSelect={() => switchScope("master")}>
            {t("assetLibrary.allManuals")}
          </ScopeTab>
        </div>
      </DrawerHeader>

      {/* Type filter + "only selectable" toggle (legacy "Nur zulässige Datei-Typen anzeigen"). */}
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <Switch isSelected={onlySelectable} onChange={setOnlySelectable}>
          {t("assetLibrary.onlySelectableFileTypes")}
        </Switch>
        {types && types.length > 0 ? <span className="text-muted-foreground text-xs">({types.join(" ")})</span> : null}
      </div>

      {/* Breadcrumb. */}
      <nav aria-label={t("assetLibrary.folderPath")} className="text-muted-foreground flex flex-wrap items-center gap-1 px-5 py-2 text-xs">
        {crumbs.map((c, i) => (
          <span key={c.id} className="flex items-center gap-1">
            {i > 0 ? <ChevronRight aria-hidden="true" className="size-3 opacity-60" /> : null}
            {i < crumbs.length - 1 ? (
              <button type="button" onClick={() => setFolderId(c.id)} className="hover:text-foreground cursor-pointer rounded-sm border-0 bg-transparent p-0 outline-none">
                {c.name}
              </button>
            ) : (
              <span className="text-foreground font-medium">{c.name}</span>
            )}
          </span>
        ))}
      </nav>

      {/* The row list — folders first, then files (legacy layout). */}
      <ul className="scroll-thin min-h-0 flex-1 list-none overflow-auto px-2 py-1">
        {folders.map((f) => (
          <li key={`folder-${f.id}`}>
            <FolderRow folder={f} onOpen={() => setFolderId(f.id)} />
          </li>
        ))}
        {files.map((file) => (
          <FileRow key={`file-${file.fileId}`} file={file} mode={mode} draggable={docked} onPreview={() => setPreview(file)} onSelect={onSelect} />
        ))}
        {folders.length === 0 && files.length === 0 ? (
          <li className="text-muted-foreground px-3 py-6 text-center text-sm">
            {noProjectFolder ? t("assetLibrary.noProjectFilesYetUploadOne") : t("assetLibrary.thisFolderIsEmpty")}
          </li>
        ) : null}
      </ul>

      {/* Upload (ADR-025 A2 — images) + create-folder (still deferred: folder management). */}
      <div className="border-border flex flex-col gap-2 border-t px-5 py-3">
        {upload.isError ? (
          <p role="alert" className="text-destructive text-xs">
            {upload.error.message}
          </p>
        ) : uploadedName ? (
          <p className="text-muted-foreground text-xs">
            <Trans i18nKey="assetLibrary.uploadedProcessing" components={{ name: <span className="text-foreground font-medium">{uploadedName}</span> }} />
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <DeferredActionButton label={t("assetLibrary.addFolder")} tooltip={t("assetLibrary.folderManagementSoon")} variant="outline" icon={<FolderPlus aria-hidden="true" className="size-3.5" />} />
          {/* Hidden when NOTHING this pick accepts is uploadable (a 3D-model pick: the server stores no model format),
              because every possible choice would 415. An absent control is honest; one that always fails is not. */}
          {canUpload ? (
            <>
              <input ref={fileInputRef} type="file" accept={uploadAccept} className="hidden" tabIndex={-1} aria-hidden="true" onChange={onPickFile} />
              <Button variant="outline" onPress={() => fileInputRef.current?.click()} isDisabled={upload.isPending} className="dark:border-border gap-1.5">
                {upload.isPending ? <Loader2 aria-hidden="true" className="size-3.5 animate-spin" /> : <Upload aria-hidden="true" className="size-3.5" />}
                {upload.isPending ? t("assetLibrary.uploading") : t("assetLibrary.uploadImage")}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {preview ? <AssetPreviewOverlay file={preview} onClose={() => setPreview(null)} /> : null}
    </Drawer>
  );
}

/** A segmented scope tab (react-aria-free — a plain button in the `role="tablist"`). Selected = the raised `--background`
 *  pill (inverts per theme); unselected = muted, hover-lifts. Reset the border explicitly (Preflight is excluded). */
function ScopeTab({ selected, onSelect, disabled = false, children }: { selected: boolean; onSelect: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "focus-visible:ring-ring/50 rounded-md border-0 px-3 py-1 outline-none focus-visible:ring-2",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        selected ? "bg-background text-foreground font-medium shadow-sm" : "text-muted-foreground hover:text-foreground bg-transparent",
      )}
    >
      {children}
    </button>
  );
}

/** A child-folder row. An **empty** folder (no files in its subtree) is DISABLED in 11a — nothing to pick — with a tooltip;
 *  uploads into empty folders (11b) will re-enable navigation. A non-empty folder descends on click. */
function FolderRow({ folder, onOpen }: { folder: AssetFolderNode; onOpen: () => void }) {
  const { t } = useTranslation();
  if (!folder.hasChildren) {
    return (
      <TooltipTrigger delay={0}>
        <span
          role="button"
          aria-disabled="true"
          tabIndex={0}
          className="focus-visible:ring-ring/50 flex w-full cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 opacity-50 outline-none focus-visible:ring-2"
        >
          <FolderIcon aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{folder.name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">{t("assetLibrary.empty")}</span>
        </span>
        <Tooltip>{t("assetLibrary.emptyFolderSoon")}</Tooltip>
      </TooltipTrigger>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="hover:bg-muted focus-visible:ring-ring/50 flex w-full cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2 text-left outline-none focus-visible:ring-2"
    >
      <FolderIcon aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{folder.name}</span>
      <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
    </button>
  );
}

/** One file row: type icon (or thumbnail) · name · size · 👁 preview; in **pick** mode a selectable file also gets a "Use"
 *  action. Hovering an image/video (with a thumbnail) name pops a floating **image preview** (legacy parity). */
function FileRow({ file, mode, draggable = false, onPreview, onSelect }: { file: AssetFile; mode: "manage" | "pick"; draggable?: boolean; onPreview: () => void; onSelect?: (f: AssetFile) => void }) {
  const { t } = useTranslation();
  const Icon = KIND_ICON[file.kind];
  const dim = mode === "pick" && !file.selectable;
  // Hover-preview source: images use thumbnail (or raw); videos use ONLY the poster/thumbnail (never raw video bytes in an
  // <img> — panel R2-7). No thumbnail ⇒ no hover preview (the row still gets the 👁 lightbox when a raw URL exists).
  const previewSrc = file.kind === "image" ? (file.thumbUrl ?? file.rawUrl) : file.kind === "video" ? file.thumbUrl : null;
  const rowRef = useRef<HTMLLIElement>(null);
  // A floating hover preview (legacy parity) — PORTALLED to the body + fixed-positioned so it escapes the list's `overflow`
  // clip and floats to the LEFT of the row (over the editor, since the drawer hugs the right edge). Plain onMouseEnter (no
  // react-aria warmup); hover-only mouse enhancement — keyboard/SR users get the full 👁 lightbox instead.
  const [rect, setRect] = useState<DOMRect | null>(null);
  return (
    <li
      ref={rowRef}
      onMouseEnter={() => previewSrc && setRect(rowRef.current?.getBoundingClientRect() ?? null)}
      onMouseLeave={() => setRect(null)}
      // The mouse sends no leave during a drag, and the floating preview would stay on top of the drop targets.
      {...(draggable && !dim ? sideDragProps({ kind: "file", fileId: file.fileId, name: file.name }, () => setRect(null)) : {})}
      className={cn("group/row hover:bg-muted flex items-center gap-3 rounded-md px-3 py-2", dim && "opacity-40", draggable && !dim && "cursor-grab")}
    >
      {file.thumbUrl && file.kind === "image" ? (
        <img src={file.thumbUrl} alt="" className="border-border pointer-events-none size-8 shrink-0 rounded object-cover" />
      ) : (
        <Icon aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
      {rect && previewSrc
        ? createPortal(
            <div
              className="border-border bg-popover pointer-events-none fixed z-[100] rounded-lg border p-1 shadow-xl"
              style={{ top: rect.top + rect.height / 2, left: rect.left - 12, transform: "translate(-100%, -50%)" }}
            >
              <img src={previewSrc} alt="" className="block h-40 w-52 rounded-md object-cover" />
            </div>,
            document.body,
          )
        : null}
      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{fmtSize(file.sizeBytes)}</span>
      {file.rawUrl ? (
        <Button variant="ghost" size="icon-sm" aria-label={t("assetLibrary.previewName", { name: file.name })} onPress={onPreview}>
          <Eye aria-hidden="true" className="size-4" />
        </Button>
      ) : null}
      {mode === "pick" && !dim ? (
        <Button variant="outline" size="xs" onPress={() => onSelect?.(file)} className="dark:border-border">
          {t("assetLibrary.use")}
        </Button>
      ) : null}
    </li>
  );
}

/** Full-size preview of a picked asset (image/video + close) — the legacy 👁 lightbox, minus the step-media actions. */
function AssetPreviewOverlay({ file, onClose }: { file: AssetFile; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <ModalOverlay isDismissable isOpen onOpenChange={(o) => !o && onClose()} className="fixed inset-0 z-[70] flex flex-col bg-black/80 p-6">
      <Modal className="flex min-h-0 w-full flex-1 flex-col outline-hidden">
        <Dialog aria-label={t("assetLibrary.previewName2", { name: file.name })} className="flex min-h-0 flex-1 flex-col outline-none">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="truncate text-sm font-medium text-white">{file.name}</span>
            <Button variant="ghost" size="icon-lg" aria-label={t("assetLibrary.closePreview")} onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
              <X aria-hidden="true" />
            </Button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center">
            {file.kind === "image" && file.rawUrl ? (
              <img src={file.rawUrl} alt={file.name} className="max-h-full max-w-full object-contain" />
            ) : file.kind === "video" && file.rawUrl ? (
              <video src={file.rawUrl} controls className="max-h-full max-w-full" />
            ) : (
              <p className="text-white/80">{t("assetLibrary.cantPreviewFileType")}</p>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
